import { PolicyDefinition } from "./policyTypes";

export const polClinical: PolicyDefinition[] = [
  {
    id: "clin-meds",
    code: "CLIN-001",
    title: "Medication Management and Controlled Drugs",
    category: "clinical",
    cqcFocus: "Safe",
    sop: true,
    version: "4.0",
    reviewMonths: 12,
    summary: "Covers ordering, storage, administration, recording and disposal of medicines, with strict controlled drug controls and a learning culture for errors.",
    keyPoints: [
      "Only trained and assessed staff administer medicines.",
      "MAR entries are signed at the time of administration, never before.",
      "Controlled drugs are double locked and the book balanced each shift by two staff.",
      "All errors and near misses are recorded and reviewed.",
    ],
    relatedRegulations: [
      "Regulation 12 Safe Care and Treatment",
      "Misuse of Drugs Regulations 2001",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Administer medicines only if trained and competency assessed.",
        "2. Check the MAR and administer at the prescribed time.",
        "3. Sign at the time of administration; never pre-sign.",
        "4. Record refusals and missed doses; inform manager and pharmacy where needed.",
        "5. Order on time and check deliveries against the order.",
        "6. Store medicines locked; fridge items at 2 to 8 C with daily temperature record.",
        "7. Keep controlled drugs in the double locked safe; balance the book each shift with two staff.",
        "8. Record every error and near miss, report and review for learning.",
        "9. Return expired and stopped medicines to the pharmacy for disposal.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Registered Manager completes a medicines audit every month.",
        "Staff competency is assessed annually and after any error.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly MAR audit with scores and actions.",
        "CD book balanced every shift and checked by the manager weekly.",
      ]},
    ],
  },
  {
    id: "clin-falls",
    code: "CLIN-002",
    title: "Falls Prevention, Response and Review",
    category: "clinical",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Reduces falls through risk assessment, a safe environment and hourly rounding, and ensures a safe, dignified response and review after every fall.",
    keyPoints: [
      "Falls risk is assessed on admission and after every fall.",
      "After a fall the person is checked for injury before being moved.",
      "Call bells, footwear, lighting and rounding are part of prevention.",
      "Falls trends are reviewed every month.",
    ],
    relatedRegulations: [
      "Regulation 12 Safe Care and Treatment",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Assess falls risk on admission and review after each fall or change.",
        "2. Put the prevention plan in place: lighting, footwear, call bell, rounding.",
        "3. After a fall, check for injury before moving; do not rush to lift.",
        "4. Record the fall and inform family and GP where needed.",
        "5. Hold a post-fall review within 72 hours and update the plan.",
        "6. Review falls trends at the monthly meeting.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "All staff contribute to rounding and prevention.",
        "Manager chairs the monthly falls review.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Falls rate tracked monthly per resident.",
        "Post fall reviews completed within 72 hours and audited.",
      ]},
    ],
  },
  {
    id: "clin-skin",
    code: "CLIN-003",
    title: "Skin Integrity and Pressure Ulcer Prevention",
    category: "clinical",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Prevents pressure damage through risk scoring, repositioning charts, the right surfaces, skin checks each shift and prompt escalation.",
    keyPoints: [
      "Risk score completed on admission and weekly.",
      "Skin checks every shift and recorded.",
      "Repositioning follows the chart; refusals are recorded.",
      "Category 2 or above is escalated the same day.",
    ],
    relatedRegulations: [
      "Regulation 12 Safe Care and Treatment",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Complete a pressure risk score on admission and weekly.",
        "2. Inspect skin every shift and record findings.",
        "3. Reposition exactly as charted; record any refusal.",
        "4. Provide the correct pressure relieving surface for the score.",
        "5. Support nutrition and hydration for skin health.",
        "6. Escalate category 2 or above to GP or tissue viability nurse the same day.",
        "7. Review surfaces and the care plan weekly.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Care staff complete checks and chart entries.",
        "Manager audits skin care monthly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly skin integrity audit.",
        "Prevalence check reported quarterly.",
      ]},
    ],
  },
  {
    id: "clin-diabetes",
    code: "CLIN-004",
    title: "Diabetes Care and Hypoglycaemia Response",
    category: "clinical",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Keeps residents with diabetes safe through structured monitoring, correct insulin handling, daily foot care and a fast hypo response.",
    keyPoints: [
      "Blood glucose is monitored and recorded as per care plan.",
      "Insulin is stored correctly and given only by trained staff.",
      "A hypo below 4 is treated immediately and rechecked in 15 minutes.",
      "Feet are checked daily and any break in skin escalated.",
    ],
    relatedRegulations: [
      "Regulation 12 Safe Care and Treatment",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Monitor blood glucose as per care plan and record the result.",
        "2. Store insulin correctly; only trained staff administer it.",
        "3. Treat a hypo below 4 with fast acting glucose, recheck in 15 minutes and escalate if not recovered.",
        "4. Check feet daily and record; escalate any blister or break in skin.",
        "5. Track HbA1c and attend the annual diabetes review.",
        "6. Follow the diabetic diet and mealtime advice from the dietitian.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Trained staff manage monitoring and insulin.",
        "Manager reviews hypo and hyper events monthly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Hypo and hyper events trended monthly.",
        "Foot check compliance audited weekly.",
      ]},
    ],
  },
  {
    id: "clin-eol",
    code: "CLIN-005",
    title: "End of Life and Palliative Care",
    category: "clinical",
    cqcFocus: "Caring",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Ensures a person centred, dignified end of life with wishes recorded, comfort prioritised and families supported before and after death.",
    keyPoints: [
      "Advance care plans are recorded and reviewed with the person and family.",
      "Spiritual, cultural and religious needs are respected.",
      "Families are supported to be present.",
      "Last offices are carried out with dignity and paperwork completed in time.",
    ],
    relatedRegulations: [
      "Regulation 9 Person Centred Care",
      "Regulation 10 Dignity and Respect",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Complete an advance care plan with the person and their family.",
        "2. Record DNACPR decisions only after proper consultation.",
        "3. Coordinate care with the GP and palliative team.",
        "4. Review comfort, mouth care and positioning every shift.",
        "5. Support family presence and keep them informed.",
        "6. After death, carry out last offices with dignity and complete paperwork within timescales.",
        "7. Offer bereavement support and signposting to the family.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "All staff deliver comfort care with dignity.",
        "Registered Manager audits end of life records annually.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Annual audit of end of life care records.",
        "Family feedback sought after each bereavement.",
      ]},
    ],
  },
  {
    id: "clin-nutrition",
    code: "CLIN-006",
    title: "Nutrition and Hydration",
    category: "clinical",
    cqcFocus: "Caring",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Prevents malnutrition and dehydration through screening, weights, fluid rounds, fortified diets and dignified mealtime support.",
    keyPoints: [
      "MUST screening on admission and weekly.",
      "Weights monthly, weekly for those at risk.",
      "Fluid rounds every shift with intake recorded.",
      "Texture modified diets follow speech and language therapy advice.",
    ],
    relatedRegulations: [
      "Regulation 12 Safe Care and Treatment",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Screen nutrition risk on admission and weekly.",
        "2. Weigh monthly, weekly for at risk residents; escalate weight loss.",
        "3. Run fluid rounds every shift and record intake.",
        "4. Fortify food and offer choices in line with the dietitian.",
        "5. Serve texture modified diets exactly as advised and train staff on them.",
        "6. Support mealtimes with dignity; protect mealtimes from interruptions.",
        "7. Refer to dietitian and GP when intake drops.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Kitchen and care staff share responsibility for intake.",
        "Manager reviews weights and charts monthly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly nutrition audit.",
        "Hydration charts checked daily by the senior on shift.",
      ]},
    ],
  },
];